import { useCallback, useSyncExternalStore } from 'react';

/**
 * M-10 최근 검색 — 브라우저 localStorage에만 저장하고 서버에 올리지 않는다.
 * 최대 10건, 같은 검색어는 지우고 맨 앞에 다시 넣는다.
 * 여러 컴포넌트가 같은 목록을 보도록 모듈 단위 스토어 + useSyncExternalStore로 둔다.
 */
const STORAGE_KEY = 'rudalink-web:recent-drug-searches';
const MAX_ITEMS = 10;
const EMPTY: string[] = [];

const listeners = new Set<() => void>();
let cache: string[] | null = null;

const read = (): string[] => {
  if (cache) return cache;
  try {
    const parsed: unknown = JSON.parse(
      localStorage.getItem(STORAGE_KEY) ?? '[]',
    );
    cache = Array.isArray(parsed)
      ? parsed.filter((v): v is string => typeof v === 'string')
      : EMPTY;
  } catch {
    cache = EMPTY;
  }
  return cache;
};

const write = (next: string[]) => {
  cache = next;
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(next));
  } catch {
    // 저장소를 못 쓰는 환경(사생활 보호 모드 등)에서는 이번 세션 메모리에만 둔다
  }
  listeners.forEach((l) => l());
};

const subscribe = (listener: () => void) => {
  listeners.add(listener);
  return () => listeners.delete(listener);
};

export const useRecentSearches = () => {
  const items = useSyncExternalStore(subscribe, read, () => EMPTY);

  const add = useCallback((keyword: string) => {
    const k = keyword.trim();
    if (!k) return;
    write([k, ...read().filter((v) => v !== k)].slice(0, MAX_ITEMS));
  }, []);

  const remove = useCallback((keyword: string) => {
    write(read().filter((v) => v !== keyword));
  }, []);

  const clear = useCallback(() => write([]), []);

  return { items, add, remove, clear };
};
